import React from 'react';
import { useNavigate } from 'react-router-dom';

const SuperAdminPage = () => {
  const navigate = useNavigate();
  // Mock data for super admin dashboard
  const stats = [
    { name: 'Total Companies', value: '12', change: '+2', icon: '🏢' },
    { name: 'Total Users', value: '186', change: '+14', icon: '👥' },
    { name: 'Platform Revenue', value: '$24,856', change: '+8%', icon: '💰' },
    { name: 'Server Status', value: 'Healthy', change: '99.9% uptime', icon: '🖥️' }
  ];
  
  const recentAlerts = [
    { type: 'warning', message: 'High CPU usage detected on main server', time: '12 minutes ago' },
    { type: 'success', message: 'Database backup completed successfully', time: '1 hour ago' },
    { type: 'error', message: 'API rate limit exceeded for company Acme Inc.', time: '3 hours ago' },
    { type: 'info', message: 'New company registered: TechStart LLC', time: '1 day ago' },
    { type: 'warning', message: 'Suspicious login attempts detected', time: '2 days ago' }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Super Admin Dashboard</h1>
        <div className="flex space-x-2">
          <button className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500">
            System Reports
          </button>
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500">
            Global Settings
          </button>
        </div>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-gray-500 text-sm">{stat.name}</p>
                <p className="text-2xl font-bold mt-1">{stat.value}</p>
                <p className="text-green-500 text-sm mt-1">{stat.change}</p>
              </div>
              <div className="text-3xl">{stat.icon}</div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* System Alerts */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-lg">System Alerts</h2>
          </div>
          <div className="p-6">
            <ul className="divide-y divide-gray-200">
              {recentAlerts.map((alert, index) => (
                <li key={index} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex space-x-3">
                    <div className="flex-shrink-0">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center 
                        ${alert.type === 'warning' ? 'bg-yellow-100 text-yellow-600' : 
                          alert.type === 'error' ? 'bg-red-100 text-red-600' : 
                          alert.type === 'success' ? 'bg-green-100 text-green-600' : 
                          'bg-blue-100 text-blue-600'}`}>
                        {alert.type === 'warning' ? '⚠️' : 
                          alert.type === 'error' ? '❌' : 
                          alert.type === 'success' ? '✅' : 
                          'ℹ️'}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{alert.message}</p>
                      <p className="text-xs text-gray-400">{alert.time}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4 text-center">
              <button className="text-primary-600 hover:text-primary-800 text-sm font-medium">
                View All Alerts
              </button>
            </div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-lg">Administrator Tools</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <button 
                onClick={() => navigate('/app/superadmin/companies')}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-gray-500">🏢</span>
                  <span>Manage Companies</span>
                </div>
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              <button 
                onClick={() => navigate('/app/admin/users')}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-gray-500">👤</span>
                  <span>Manage Admin Users</span>
                </div>
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors">
                <div className="flex items-center space-x-3">
                  <span className="text-gray-500">⚙️</span>
                  <span>Global Configuration</span>
                </div>
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors">
                <div className="flex items-center space-x-3">
                  <span className="text-gray-500">📊</span>
                  <span>System Analytics</span>
                </div>
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors">
                <div className="flex items-center space-x-3">
                  <span className="text-gray-500">🔒</span>
                  <span>Security Center</span>
                </div>
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Server Status */}
      <div className="mt-6 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-lg">Server Status</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">API Server</h3>
                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Operational</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-full rounded-full bg-green-500" style={{ width: '92%' }}></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">CPU Load: 8%, Memory: 42%</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">Database</h3>
                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Operational</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-full rounded-full bg-green-500" style={{ width: '68%' }}></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">CPU Load: 15%, Memory: 68%</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">File Storage</h3>
                <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Warning</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full">
                <div className="h-full rounded-full bg-yellow-500" style={{ width: '87%' }}></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Disk Usage: 87%, I/O: Normal</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPage;
